import { useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { MessageTemplateEditor } from '@/components/instagram/MessageTemplateEditor'

interface Connection {
  id: string
  ig_username: string | null
  is_active: boolean
}

interface Rule {
  id: string
  name: string
  connection_id: string
  trigger_type: string
  keyword: string
  message_template: string
  is_active: boolean
  cooldown_hours: number
  priority: number
  post_id: string | null
  link_id: string | null
}

interface ShortLink {
  id: string
  shortcode: string
  original_url: string
  title: string | null
}

const triggerTypes = [
  { value: 'exact', label: 'Exact Match', description: 'Comment must exactly match the keyword' },
  { value: 'starts_with', label: 'Starts With', description: 'Comment starts with the keyword' },
  { value: 'contains', label: 'Contains', description: 'Comment contains the keyword anywhere' },
  { value: 'regex', label: 'Regex', description: 'Comment matches a regular expression pattern' },
]

const INSTAGRAM_POST_REGEX = /instagram\.com\/p\/([A-Za-z0-9_-]+)/

/** Extract the short code from an Instagram post URL, or return null */
function extractPostId(urlOrId: string): string | null {
  const trimmed = urlOrId.trim()
  if (!trimmed) return null
  const match = trimmed.match(INSTAGRAM_POST_REGEX)
  if (match) return match[1]
  // Allow bare post IDs (no URL) — alphanumeric + underscore + dash only
  if (/^[A-Za-z0-9_-]+$/.test(trimmed)) return trimmed
  return null
}

export function InstagramRuleEditor() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new'
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch connections
  const { data: connectionsData } = useQuery<{ success: boolean; data: Connection[] }>({
    queryKey: ['ig-connections'],
    queryFn: () => apiRequest('/api/instagram/auth/connections'),
  })

  // Fetch short links
  const { data: linksData } = useQuery<{ success: boolean; data: ShortLink[] }>({
    queryKey: ['links-for-rule'],
    queryFn: () => apiRequest('/api/links?limit=50'),
  })

  // Fetch rule if editing
  const { data: ruleData } = useQuery<{ success: boolean; data: Rule }>({
    queryKey: ['ig-rule', id],
    queryFn: () => apiRequest(`/api/instagram/rules/${id}`),
    enabled: !isNew && !!id,
  })

  // Populate form when rule loads (render-time state adjustment)
  const [populatedRuleId, setPopulatedRuleId] = useState<string | undefined>()
  const [name, setName] = useState('')
  const [connectionId, setConnectionId] = useState('')
  const [triggerType, setTriggerType] = useState('contains')
  const [keyword, setKeyword] = useState('')
  const [messageTemplate, setMessageTemplate] = useState('')
  const [cooldownHours, setCooldownHours] = useState(24)
  const [priority, setPriority] = useState(0)
  const [isActive, setIsActive] = useState(true)

  // Phase 2 state
  const [postUrlInput, setPostUrlInput] = useState('')
  const [postId, setPostId] = useState<string | null>(null)
  const [postIdError, setPostIdError] = useState<string | null>(null)
  const [linkId, setLinkId] = useState<string | null>(null)

  if (ruleData?.data && ruleData.data.id !== populatedRuleId) {
    const rule = ruleData.data
    setPopulatedRuleId(rule.id)
    setName(rule.name)
    setConnectionId(rule.connection_id)
    setTriggerType(rule.trigger_type)
    setKeyword(rule.keyword)
    setMessageTemplate(rule.message_template)
    setCooldownHours(rule.cooldown_hours)
    setPriority(rule.priority ?? 0)
    setIsActive(rule.is_active)
    setPostId(rule.post_id ?? null)
    setPostUrlInput(rule.post_id ?? '')
    setLinkId(rule.link_id ?? null)
  }

  // Auto-select first connection (render-time state adjustment)
  if (isNew && connectionsData?.data?.length && !connectionId) {
    setConnectionId(connectionsData.data[0].id)
  }

  // Derive regex validation
  const regexError = useMemo(() => {
    if (triggerType === 'regex' && keyword) {
      try {
        new RegExp(keyword)
        return null
      } catch (e) {
        return (e as Error).message
      }
    }
    return null
  }, [triggerType, keyword])

  const handlePostUrlChange = (value: string) => {
    setPostUrlInput(value)
    if (!value.trim()) {
      setPostId(null)
      setPostIdError(null)
      return
    }
    const extracted = extractPostId(value)
    if (extracted) {
      setPostId(extracted)
      setPostIdError(null)
    } else {
      setPostId(null)
      setPostIdError('Could not extract a post ID from this URL. Use a URL like https://www.instagram.com/p/ABC123xyz/')
    }
  }

  const links = linksData?.data ?? []
  const selectedLink = links.find(l => l.id === linkId) ?? null

  const linkPreview = selectedLink
    ? `urlzippy.com/${selectedLink.shortcode}`
    : undefined

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      if (isNew) {
        return apiRequest('/api/instagram/rules', 'POST', data)
      }
      return apiRequest(`/api/instagram/rules/${id}`, 'PUT', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ig-rules'] })
      navigate('/instagram')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest(`/api/instagram/rules/${id}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ig-rules'] })
      navigate('/instagram')
    },
  })

  const connections = connectionsData?.data || []
  const canSave = name && connectionId && keyword && messageTemplate && !regexError && !postIdError && !saveMutation.isPending

  const handleSave = () => {
    if (!canSave) return
    saveMutation.mutate({
      name,
      connection_id: connectionId,
      trigger_type: triggerType,
      keyword,
      message_template: messageTemplate,
      cooldown_hours: cooldownHours,
      priority,
      is_active: isActive,
      post_id: postId ?? null,
      link_id: linkId ?? null,
    })
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/instagram" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <span className="material-symbols-outlined">arrow_back</span>
        </Link>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {isNew ? 'Create Rule' : 'Edit Rule'}
        </h2>
      </div>

      <div className="max-w-2xl space-y-6">
        <div className="bg-white dark:bg-[#1c2230] rounded-xl border border-[#e7ebf3] dark:border-white/10 p-5 space-y-5">
          {/* Rule name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rule Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Send pricing DM"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
            />
          </div>

          {/* Connected account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instagram Account</label>
            {connections.length === 0 ? (
              <p className="text-sm text-gray-400">
                No accounts connected. <Link to="/instagram" className="text-[#1754cf] hover:underline">Connect one first</Link>.
              </p>
            ) : (
              <select
                value={connectionId}
                onChange={e => setConnectionId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
              >
                <option value="">Select account...</option>
                {connections.map(c => (
                  <option key={c.id} value={c.id}>
                    @{c.ig_username || 'Unknown'} {!c.is_active && '(Inactive)'}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Target post (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Target Specific Post <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Paste an Instagram post URL to only trigger this rule on that post. Leave empty to match all posts.
            </p>
            <input
              type="text"
              value={postUrlInput}
              onChange={e => handlePostUrlChange(e.target.value)}
              onBlur={e => handlePostUrlChange(e.target.value)}
              placeholder="https://www.instagram.com/p/ABC123xyz/"
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white ${
                postIdError
                  ? 'border-red-400 dark:border-red-600'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {postIdError && (
              <p className="text-xs text-red-500 mt-1">{postIdError}</p>
            )}
            {postId && !postIdError && (
              <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                <span className="material-symbols-outlined text-[13px]">check_circle</span>
                Post ID: {postId}
              </span>
            )}
          </div>

          {/* Trigger type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trigger Type</label>
            <div className="grid grid-cols-2 gap-2">
              {triggerTypes.map(tt => (
                <button
                  key={tt.value}
                  type="button"
                  onClick={() => setTriggerType(tt.value)}
                  className={`p-3 text-left border rounded-lg transition-all ${
                    triggerType === tt.value
                      ? 'border-[#1754cf] bg-[#1754cf]/5'
                      : 'border-gray-200 dark:border-white/10 hover:border-gray-300'
                  }`}
                >
                  <p className={`text-xs font-medium ${triggerType === tt.value ? 'text-[#1754cf]' : 'text-gray-700 dark:text-gray-300'}`}>
                    {tt.label}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{tt.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Keyword */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Keyword</label>
            <input
              type="text"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder={triggerType === 'regex' ? '(price|pricing|cost)' : 'INFO'}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white font-mono"
            />
            {regexError && (
              <p className="text-xs text-red-500 mt-1">Invalid regex: {regexError}</p>
            )}
          </div>

          {/* Message template */}
          <MessageTemplateEditor
            value={messageTemplate}
            onChange={setMessageTemplate}
            linkPreview={linkPreview ? `https://${linkPreview}` : undefined}
          />

          {/* Attach short link (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Attach Short Link <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Select a short link to make <code className="bg-gray-100 dark:bg-white/5 px-1 rounded">{'{{link}}'}</code> available in your message template. The short URL will be inserted automatically when a DM is sent.
            </p>
            <select
              value={linkId ?? ''}
              onChange={e => setLinkId(e.target.value || null)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
            >
              <option value="">None</option>
              {links.map(l => (
                <option key={l.id} value={l.id}>
                  {l.title || l.original_url.slice(0, 50)} — urlzippy.com/{l.shortcode}
                </option>
              ))}
            </select>
            {selectedLink && (
              <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                <span className="material-symbols-outlined text-[13px]">link</span>
                urlzippy.com/{selectedLink.shortcode}
              </span>
            )}
          </div>

          {/* Cooldown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cooldown (hours)</label>
            <p className="text-xs text-gray-400 mb-2">How long to wait before sending another DM to the same user for this rule</p>
            <input
              type="number"
              value={cooldownHours}
              onChange={e => setCooldownHours(Math.max(0, parseInt(e.target.value) || 0))}
              min={0}
              className="w-32 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
            <p className="text-xs text-gray-400 mb-2">Higher number = checked first when multiple rules could match</p>
            <input
              type="number"
              value={priority}
              onChange={e => setPriority(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
              min={0}
              max={100}
              className="w-32 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white"
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-3 border-t border-gray-200 dark:border-white/10">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</p>
              <p className="text-xs text-gray-400">Enable or disable this rule</p>
            </div>
            <button
              type="button"
              onClick={() => setIsActive(!isActive)}
              className={`relative w-11 h-6 rounded-full transition-colors ${isActive ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isActive ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </div>

        {/* Actions */}
        {saveMutation.isError && (
          <p className="text-sm text-red-500">{(saveMutation.error as Error)?.message || 'Failed to save rule'}</p>
        )}

        <div className="flex items-center justify-between">
          <div>
            {!isNew && (
              <button
                onClick={() => { if (confirm('Delete this rule?')) deleteMutation.mutate() }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm text-red-600 border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Rule'}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <Link
              to="/instagram"
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg"
            >
              Cancel
            </Link>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="px-5 py-2 text-sm font-medium text-white bg-[#1754cf] rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? 'Saving...' : isNew ? 'Create Rule' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
